import { AUDIO_MUSIC, AUDIO_SOUNDS, loadSynthesizedUiSounds } from "../systems/audio.js";
import {
    SPRITEFUSION_SPRITE,
    SPRITEFUSION_SPRITESHEET_URL,
    getSpriteFusionMap,
    loadSpriteFusionMap,
} from "../systems/spritefusionMap.js";

/**
 * Asset loader — splash assets first (intro), then the rest of the game.
 */

let gameLoadStarted = false;
let mapReady = false;
let gameAssetsQueued = false;
let gameLoadPromise = null;
/** Latest progress reported by KAPLAY onLoading (0–1). */
let kaplayLoadingProgress = 0;

/** Font + RAM stick only — enough to show the intro splash immediately. */
export function loadSplashAssets() {
    loadFont("ShareTechMono", "fonts/ShareTechMono-Regular.ttf", { filter: "linear" });
    loadSprite("ram_stick", "sprites/ram stick/ram.png", {
        filter: "nearest",
    });
}

/** Everything after the splash (requires SpriteFusion map parse). */
export function loadGameAssets() {
    loadSprite("dog_idle", "sprites/dog/Idle.png", {
        sliceX: 4,
        sliceY: 1,
        filter: "nearest",
        anims: {
            idle: { from: 0, to: 3, loop: true, speed: 5 },
        },
    });
    loadSprite("dog_walk", "sprites/dog/Walk.png", {
        sliceX: 6,
        sliceY: 1,
        filter: "nearest",
        anims: {
            walk: { from: 0, to: 5, loop: true, speed: 9 },
        },
    });
    loadSprite("dog_attack", "sprites/dog/Attack.png", {
        sliceX: 4,
        sliceY: 1,
        filter: "nearest",
        anims: {
            attack: { from: 0, to: 3, loop: false, speed: 14 },
        },
    });
    loadSprite("dog_hurt", "sprites/dog/Hurt.png", {
        sliceX: 2,
        sliceY: 1,
        filter: "nearest",
        anims: {
            hurt: { from: 0, to: 1, loop: false, speed: 10 },
        },
    });
    loadSprite("dog_death", "sprites/dog/Death.png", {
        sliceX: 4,
        sliceY: 1,
        filter: "nearest",
        anims: {
            death: { from: 0, to: 3, loop: false, speed: 8 },
        },
    });

    loadSprite("mechanic_idle", "sprites/mechanic/Idle.png", {
        sliceX: 6,
        sliceY: 1,
        filter: "nearest",
        anims: {
            idle: { from: 0, to: 5, loop: true, speed: 6 },
        },
    });
    loadSprite("mechanic_walk", "sprites/mechanic/Walk.png", {
        sliceX: 10,
        sliceY: 1,
        filter: "nearest",
        anims: {
            walk: { from: 0, to: 9, loop: true, speed: 10 },
        },
    });
    loadSprite("mechanic_hurt", "sprites/mechanic/Hurt.png", {
        sliceX: 3,
        sliceY: 1,
        filter: "nearest",
        anims: {
            hurt: { from: 0, to: 2, loop: false, speed: 10 },
        },
    });
    loadSprite("mechanic_run", "sprites/mechanic/Run.png", {
        sliceX: 10,
        sliceY: 1,
        filter: "nearest",
        anims: {
            run: { from: 0, to: 9, loop: true, speed: 14 },
        },
    });
    loadSprite("suite_idle", "sprites/suite/Idle.png", {
        sliceX: 6,
        sliceY: 1,
        filter: "nearest",
        anims: {
            idle: { from: 0, to: 5, loop: true, speed: 6 },
        },
    });
    loadSprite("suite_walk", "sprites/suite/Walk.png", {
        sliceX: 10,
        sliceY: 1,
        filter: "nearest",
        anims: {
            walk: { from: 0, to: 9, loop: true, speed: 10 },
        },
    });
    loadSprite("suite_hurt", "sprites/suite/Hurt.png", {
        sliceX: 3,
        sliceY: 1,
        filter: "nearest",
        anims: {
            hurt: { from: 0, to: 2, loop: false, speed: 10 },
        },
    });
    loadSprite("suite_run", "sprites/suite/Run.png", {
        sliceX: 10,
        sliceY: 1,
        filter: "nearest",
        anims: {
            run: { from: 0, to: 9, loop: true, speed: 14 },
        },
    });
    loadSprite("homeless_idle", "sprites/homelessman/Idle_2.png", {
        sliceX: 11,
        sliceY: 1,
        filter: "nearest",
        anims: {
            idle: { from: 0, to: 10, loop: true, speed: 7 },
        },
    });
    loadSprite("homeless_hurt", "sprites/homelessman/Hurt.png", {
        sliceX: 3,
        sliceY: 1,
        filter: "nearest",
        anims: {
            hurt: { from: 0, to: 2, loop: false, speed: 10 },
        },
    });
    loadSprite("homeless_walk", "sprites/homelessman/Walk.png", {
        sliceX: 8,
        sliceY: 1,
        filter: "nearest",
        anims: {
            walk: { from: 0, to: 7, loop: true, speed: 8 },
        },
    });
    loadSprite("homeless_run", "sprites/homelessman/Run.png", {
        sliceX: 8,
        sliceY: 1,
        filter: "nearest",
        anims: {
            run: { from: 0, to: 7, loop: true, speed: 12 },
        },
    });
    loadSprite("trap", "sprites/trap/trap.png", {
        filter: "nearest",
    });

    loadSprite("keycard", "sprites/keycard/Card.png", {
        filter: "nearest",
    });

    loadSprite("wheel_bot_move", "sprites/wheel bot/move without FX.png", {
        sliceX: 1,
        sliceY: 8,
        filter: "nearest",
        anims: {
            idle: { from: 0, to: 0, loop: true },
            move: { from: 0, to: 7, loop: true, speed: 10 },
        },
    });
    loadSprite("wheel_bot_wake", "sprites/wheel bot/wake.png", {
        sliceX: 1,
        sliceY: 5,
        filter: "nearest",
        anims: {
            sleep: { from: 0, to: 0, loop: true },
            wake: { from: 0, to: 4, loop: false, speed: 10 },
        },
    });
    loadSprite("wheel_bot_damaged", "sprites/wheel bot/damaged.png", {
        sliceX: 1,
        sliceY: 2,
        filter: "nearest",
        anims: {
            damaged: { from: 0, to: 1, loop: false, speed: 10 },
        },
    });
    loadSprite("wheel_bot_charge", "sprites/wheel bot/charge.png", {
        sliceX: 1,
        sliceY: 4,
        filter: "nearest",
        anims: {
            charge: { from: 0, to: 3, loop: false, speed: 14 },
        },
    });
    loadSprite("wheel_bot_shoot", "sprites/wheel bot/shoot with FX.png", {
        sliceX: 1,
        sliceY: 4,
        filter: "nearest",
        anims: {
            shoot: { from: 0, to: 3, loop: false, speed: 14 },
        },
    });
    loadSprite("wheel_bot_death", "sprites/wheel bot/death.png", {
        sliceX: 1,
        sliceY: 6,
        filter: "nearest",
        anims: {
            death: { from: 0, to: 5, loop: false, speed: 10 },
        },
    });

    // SFX under public/sounds/
    loadSound("sfx_bark", "sounds/sfx_bark.mp3");
    loadSound("sfx_alarm_loop", "sounds/sfx_alarm_loop.mp3");
    loadSound("sfx_player_hurt", "sounds/sfx_player_hurt.ogg");
    loadSound(AUDIO_SOUNDS.robotHit, "sounds/hurt.wav");
    loadSound("sfx_robot_death", "sounds/sfx_robot_death.ogg");
    loadSound("sfx_ram_pickup", "sounds/sfx_ram_pickup.ogg");
    loadSound("sfx_memory_open", "sounds/sfx_memory_open.ogg");
    // No dedicated close clip yet — reuse open
    loadSound("sfx_memory_close", "sounds/sfx_memory_open.ogg");

    loadMusic(AUDIO_MUSIC.battleDefault, "sounds/01. Battle Theme I (loop).mp3");
    loadMusic(AUDIO_MUSIC.darkFactory, "sounds/25. Dark Factory.mp3");
    loadMusic(AUDIO_MUSIC.livelyCity, "sounds/02. Lively City.mp3");
    loadMusic(AUDIO_MUSIC.hiddenCavern, "sounds/06. Hidden Cavern.mp3");
    loadMusic(AUDIO_MUSIC.spiritsForest, "sounds/07. Spirits Forest (full).mp3");

    loadSynthesizedUiSounds();

    const map = getSpriteFusionMap();
    loadSprite(SPRITEFUSION_SPRITE, SPRITEFUSION_SPRITESHEET_URL, {
        sliceX: map.tileset.columns,
        sliceY: Math.ceil(map.tileset.tilecount / map.tileset.columns),
        filter: "nearest",
    });
}

/**
 * Fetch map.lua then queue game sprites. Safe to call once; returns a promise.
 * Call after splash assets so intro can open immediately.
 */
export function startGameAssetLoad() {
    if (gameLoadPromise) return gameLoadPromise;

    gameLoadStarted = true;
    gameLoadPromise = loadSpriteFusionMap()
        .then(() => {
            mapReady = true;
            loadGameAssets();
            gameAssetsQueued = true;
        });

    return gameLoadPromise;
}

/** True once map is parsed and game sprites have been queued with KAPLAY. */
export function areGameAssetsQueued() {
    return gameAssetsQueued;
}

export function noteKaplayLoadProgress(progress) {
    kaplayLoadingProgress = Math.max(0, Math.min(1, progress ?? 0));
}

/**
 * 0–1 boot progress for the splash RAM fill.
 * Prefers KAPLAY onLoading / loadProgress after game assets are queued.
 */
export function getBootLoadProgress() {
    if (areGameAssetsLoaded()) return 1;
    if (!gameLoadStarted) return kaplayLoadingProgress;
    if (!mapReady) return Math.max(0.04, kaplayLoadingProgress * 0.15);
    if (!gameAssetsQueued) return Math.max(0.08, kaplayLoadingProgress * 0.2);
    return Math.max(kaplayLoadingProgress, loadProgress());
}

/** True when KAPLAY reports all queued assets finished. */
export function areGameAssetsLoaded() {
    return gameAssetsQueued && loadProgress() >= 1;
}

/** @deprecated Prefer loadSplashAssets + startGameAssetLoad */
export function loadAssets() {
    loadSplashAssets();
    loadGameAssets();
}
